/**
 * Where meeting content goes, and what Kizuna AI keeps.
 *
 * One idea has to land in the first screen: meeting content travels only
 * between your device and the AI provider you chose, and Kizuna AI handles
 * accounts and billing. The three cards say it the way people actually use
 * Sokuji (own key, Kizuna AI account, on-device), each with a small flow; the
 * one exception (managed voice-clone samples) sits right under them, not in
 * small print.
 *
 * Below the fold the page is an inventory, and it must be a true one: every
 * row is backed by the facts in
 * docs/superpowers/specs/2026-10-25-privacy-page-redesign-design.md §5.
 * `privacyClaims.test.ts` pins the retired overclaims as gone.
 *
 * Every block is built from the id lists in privacySections.ts, so a locale
 * that lacks a key fails siteKeys.test.ts instead of rendering the key.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useI18n, useLocalizedPath } from '@/lib/i18n';
import { PageHead, Section, Table } from '@/components/site/Page';
import '@/components/site/Page.scss';
import {
  PRIVACY_CHECKS,
  PRIVACY_DEST_DEVICE,
  PRIVACY_DEST_KIZUNA,
  PRIVACY_DEST_PROVIDER,
  PRIVACY_PROMISES,
  PRIVACY_STORED,
  PRIVACY_THIRD_PARTIES,
  PRIVACY_WAYS,
} from '@/components/site/privacySections';
import './Privacy.scss';

type Way = (typeof PRIVACY_WAYS)[number];

/** One hop of a mini flow: a solid line for content, a dashed one for the account. */
function Hop({ from, to, kind }: { from: string; to: string; kind: 'content' | 'account' }) {
  return (
    <div className={`privacy__flow privacy__flow--${kind}`}>
      <span className="privacy__node">{from}</span>
      <span className="privacy__line" aria-hidden="true" />
      <span className="privacy__node">{to}</span>
    </div>
  );
}

function WayCard({ id }: { id: Way }) {
  const { t } = useI18n();
  const device = t('privacyPage.node.device');
  const provider = t('privacyPage.node.provider');

  return (
    <article className={`privacy__way${id === 'kizuna' ? ' privacy__way--highlight' : ''}`}>
      <h3>{t(`privacyPage.way.${id}.title`)}</h3>
      <p>{t(`privacyPage.way.${id}.body`)}</p>
      <div className="privacy__flows">
        {id === 'local' ? (
          <div className="privacy__flow privacy__flow--stays">
            <span className="privacy__node">{device}</span>
            <span className="privacy__stays">{t('privacyPage.node.stays')}</span>
          </div>
        ) : (
          <Hop from={device} to={provider} kind="content" />
        )}
        {id === 'kizuna' && <Hop from={device} to={t('privacyPage.node.kizuna')} kind="account" />}
      </div>
      <p className="privacy__way-note">{t(`privacyPage.way.${id}.note`)}</p>
    </article>
  );
}

function DestColumn({
  column,
  items,
  children,
}: {
  column: 'provider' | 'kizuna' | 'device';
  items: readonly string[];
  children?: ReactNode;
}) {
  const { t } = useI18n();
  return (
    <article className={`privacy__dest privacy__dest--${column}`}>
      <h3>{t(`privacyPage.dest.${column}.title`)}</h3>
      <p className="privacy__dest-who">{t(`privacyPage.dest.${column}.who`)}</p>
      <ul>
        {items.map((id) => (
          <li key={id}>{t(`privacyPage.dest.${column}.${id}`)}</li>
        ))}
      </ul>
      <p className="privacy__dest-foot">
        {t(`privacyPage.dest.${column}.foot`)}
        {children}
      </p>
    </article>
  );
}

export function Privacy() {
  const { t } = useI18n();
  const lp = useLocalizedPath();

  return (
    <div className="site-page privacy">
      <PageHead
        kicker={t('privacyPage.hero.kicker')}
        title={t('privacyPage.hero.title')}
        lead={t('privacyPage.hero.lead')}
      />

      <div className="privacy__ways" role="group" aria-label={t('privacyPage.hero.flowLabel')}>
        {PRIVACY_WAYS.map((id) => (
          <WayCard key={id} id={id} />
        ))}
      </div>
      <p className="privacy__exception">{t('privacyPage.hero.exception')}</p>

      <Section title={t('privacyPage.dest.title')} lead={t('privacyPage.dest.lead')}>
        <div className="privacy__dests">
          <DestColumn column="provider" items={PRIVACY_DEST_PROVIDER}>
            {' '}
            <Link to={lp('/providers')}>{t('privacyPage.dest.provider.link')}</Link>
          </DestColumn>
          <DestColumn column="kizuna" items={PRIVACY_DEST_KIZUNA} />
          <DestColumn column="device" items={PRIVACY_DEST_DEVICE} />
        </div>
        <p className="privacy__contacts">{t('privacyPage.dest.contacts')}</p>
        <div className="privacy__thirds">
          {PRIVACY_THIRD_PARTIES.map((id) => (
            <article className="privacy__third" key={id}>
              <h3>{t(`privacyPage.third.${id}.title`)}</h3>
              <p>{t(`privacyPage.third.${id}.body`)}</p>
            </article>
          ))}
        </div>
      </Section>

      <Section title={t('privacyPage.stored.title')} lead={t('privacyPage.stored.lead')}>
        <Table
          head={[
            t('privacyPage.stored.col.data'),
            t('privacyPage.stored.col.why'),
            t('privacyPage.stored.col.keep'),
          ]}
          widths="1.2fr 1.2fr 1fr"
          rows={PRIVACY_STORED.map((id) => [
            t(`privacyPage.stored.${id}.data`),
            t(`privacyPage.stored.${id}.why`),
            t(`privacyPage.stored.${id}.keep`),
          ])}
        />
        <p className="privacy__note">{t('privacyPage.stored.deletion')}</p>
      </Section>

      <Section title={t('privacyPage.check.title')} lead={t('privacyPage.check.lead')}>
        <div className="privacy__checks">
          {PRIVACY_CHECKS.map((id) => (
            <article className="privacy__check" key={id}>
              <h3>{t(`privacyPage.check.${id}.title`)}</h3>
              <p>{t(`privacyPage.check.${id}.body`)}</p>
            </article>
          ))}
        </div>
      </Section>

      <Section title={t('privacyPage.promise.title')}>
        <ul className="privacy__promises">
          {PRIVACY_PROMISES.map((id) => (
            <li key={id}>{t(`privacyPage.promise.${id}`)}</li>
          ))}
        </ul>
      </Section>

      <div className="privacy__end">
        <p>{t('privacyPage.end.note')}</p>
        <Link className="privacy__end-cta" to={lp('/docs/privacy')}>
          {t('privacyPage.end.cta')} <span aria-hidden="true">&rarr;</span>
        </Link>
      </div>
    </div>
  );
}
